/**
 * useFlightStatusAutomation
 *
 * Runs inside FlightDetailScreen (TrackFlightView). Watches live FR24 data
 * and applies automated flight + quote status transitions:
 *
 *  POSITIONING / READY / DELAYED → IN_TRANSIT   (aircraft airborne, alt > 1000 ft)
 *  IN_TRANSIT  → COMPLETED                       (landed at expected destination)
 *  IN_TRANSIT  → DIVERTED                        (landed at different airport)
 *  Any pre-flight → DELAYED                      (departure time + 2 h passed)
 *
 * Quote status follows:
 *  Any flight IN_TRANSIT  → Quote IN_TRANSIT
 *  ALL flights COMPLETED  → Quote COMPLETED
 *  Any flight DIVERTED    → Quote DIVERTED
 *  ALL flights CANCELLED  → Quote CANCELLED
 */
import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { deriveQuoteStatus, resolveDepartedFromSummary } from '@/features/track/utils/flightStatusUtils';
import { isPastReviewThreshold, markNeedsReview, shouldSkipFr24 } from '@/features/track/utils/needsReview';

const PRE_FLIGHT = ['PENDING', 'POSITIONING', 'READY', 'DELAYED'];
const AIRBORNE_ALT_FT = 1000;

// API returns flight_id / quote_id; codebase historically referenced .id.
// Resolve either shape so the automation works regardless.
const getFlightId = (f) => f?.flight_id || f?.id;
const getQuoteId = (f) => f?.quote_id || f?.quote?.quote_id || f?.quote?.id;

export function useFlightStatusAutomation(flight, liveData) {
  const queryClient = useQueryClient();
  const transitioningRef = useRef(false);
  const appliedStatusRef = useRef(null); // prevent duplicate transitions
  const landingCheckActiveRef = useRef(false);
  const flightId = getFlightId(flight);
  const quoteId = getQuoteId(flight);

  // Reset per-render guards whenever the DB status updates
  useEffect(() => {
    appliedStatusRef.current = null;
    landingCheckActiveRef.current = false;
  }, [flight?.flight_status]);

  const applyTransition = useCallback(async (newStatus) => {
    if (!flightId || transitioningRef.current) return;

    const current = (flight?.flight_status || '').toUpperCase();
    const target = newStatus.toUpperCase();
    if (current === target || appliedStatusRef.current === target) return;

    appliedStatusRef.current = target;
    transitioningRef.current = true;

    console.log(`[StatusAutomation] PATCH flight ${flightId} ${current} → ${target}`);

    // Optimistic UI update — flip the cached flight_status now so the
    // badge updates without waiting for the round-trip.
    const patchCached = (status) => {
      queryClient.setQueryData(['flightDetails', flightId], (old) => {
        if (!old) return old;
        const apply = (f) =>
          (f?.flight_id === flightId || f?.id === flightId)
            ? { ...f, flight_status: status }
            : f;
        return Array.isArray(old) ? old.map(apply) : apply(old);
      });
    };
    patchCached(target);

    try {
      await apiClient.patchFlight(flightId, { flight_status: target });
      console.log(`[StatusAutomation] PATCH ok ${flightId} → ${target}`);

      // Cascade to quote
      if (quoteId) {
        const newQuoteStatus = await deriveQuoteStatus(quoteId, flightId, target);
        if (newQuoteStatus) {
          await apiClient.updateQuote(quoteId, { status: newQuoteStatus });
        }
      }

      queryClient.invalidateQueries({ queryKey: ['flights'] });
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
    } catch (err) {
      console.error('[StatusAutomation] Transition failed:', err);
      // Rollback the optimistic flip so the UI matches reality
      patchCached(current);
      appliedStatusRef.current = null; // allow retry
    } finally {
      transitioningRef.current = false;
    }
  }, [flightId, flight?.flight_status, quoteId, queryClient]);

  // Before marking a flight DELAYED on the clock, ask FR24 whether it actually
  // departed. If an ended summary matches the route → COMPLETED/DIVERTED; if
  // it's mid-flight → IN_TRANSIT. When FR24 is silent: past 24h → flag for human
  // review (and stop querying); otherwise → DELAYED.
  const maybeMarkDelayed = useCallback(async () => {
    if (shouldSkipFr24(flight)) return; // already flagged needs-review
    const fromSummary = await resolveDepartedFromSummary(flight);
    if (fromSummary) { applyTransition(fromSummary); return; }
    if (isPastReviewThreshold(flight)) { markNeedsReview(flight); return; }
    applyTransition('DELAYED');
  }, [flight, applyTransition]);

  // ── DELAYED: time-triggered, FR24-gated ──────────────────────────────────
  useEffect(() => {
    if (!flightId || !flight?.departure_date) return;
    const status = (flight.flight_status || '').toUpperCase();
    if (['DELAYED', 'IN_TRANSIT', 'COMPLETED', 'DIVERTED', 'LAYOVER', 'CANCELLED'].includes(status)) return;

    const delayedAt = new Date(flight.departure_date).getTime() + 2 * 60 * 60 * 1000;
    const msUntil = delayedAt - Date.now();

    if (msUntil <= 0) {
      maybeMarkDelayed();
      return;
    }

    const t = setTimeout(() => maybeMarkDelayed(), msUntil);
    return () => clearTimeout(t);
  }, [flightId, flight?.flight_status, flight?.departure_date, maybeMarkDelayed]);

  // ── Live data transitions ─────────────────────────────────────────────────
  useEffect(() => {
    if (!liveData || !flightId) {
      console.log('[StatusAutomation] skip: no liveData or flight', { hasLiveData: !!liveData, flightId });
      return;
    }

    const status = (flight?.flight_status || '').toUpperCase();
    const alt = liveData.alt ?? 0;
    const gspeed = liveData.gspeed ?? 0;
    const vspeed = liveData.vspeed ?? 0;
    console.log('[StatusAutomation] tick', { status, alt, gspeed, vspeed, reg: liveData.reg });

    // Pre-flight → IN_TRANSIT when airborne
    if (PRE_FLIGHT.includes(status)) {
      if (alt > AIRBORNE_ALT_FT) {
        // Verify route so we don't pick up a passing aircraft
        const liveOrig = (liveData.orig_icao || liveData.orig_iata || '').toUpperCase();
        const liveDest = (liveData.dest_icao || liveData.dest_iata || '').toUpperCase();
        const depAp = (flight?.departure_airport || '').toUpperCase();
        const arrAp = (flight?.arrival_airport || '').toUpperCase();

        const routeOk =
          !liveOrig || !liveDest || // can't verify — trust the altitude
          liveOrig === depAp ||
          liveDest === arrAp;

        console.log('[StatusAutomation] pre-flight airborne check', { liveOrig, liveDest, depAp, arrAp, routeOk });

        if (routeOk) {
          landingCheckActiveRef.current = false;
          applyTransition('IN_TRANSIT');
        }
      } else {
        console.log('[StatusAutomation] pre-flight but alt below 1000ft', { alt });
      }
      return;
    }

    // IN_TRANSIT → landing detection.
    // Only treat the aircraft as landed when FR24 actually reports zero ground
    // AND vertical speed. Missing telemetry must NOT be read as "stopped" — the
    // `?? 0` fallback above would otherwise turn a data gap on an airborne
    // flight into a false landing. A genuinely cruising aircraft never reports
    // gspeed 0, so requiring real (finite) zeros is a safe landed signal.
    if (status === 'IN_TRANSIT') {
      const hasSpeedData =
        Number.isFinite(liveData.gspeed) && Number.isFinite(liveData.vspeed);
      const hasLanded = hasSpeedData && liveData.gspeed === 0 && liveData.vspeed === 0;
      if (hasLanded && !landingCheckActiveRef.current) {
        landingCheckActiveRef.current = true;
        confirmLanding(flight, liveData).then((transition) => {
          if (transition) applyTransition(transition);
          else landingCheckActiveRef.current = false; // check failed — retry on next tick
        });
      }
    }
  }, [liveData, flightId, flight, applyTransition]);
}

// ── Fetch FR24 summary to confirm COMPLETED vs DIVERTED ──────────────────────
async function confirmLanding(flight, liveData) {
  try {
    const tailNumber = flight.aircraft?.tail_number || liveData.reg;
    if (!tailNumber) return 'COMPLETED';

    const now = new Date();
    const from = new Date(now.getTime() - 12 * 60 * 60 * 1000).toISOString();
    const to = now.toISOString();

    const summaryData = await apiClient.getFlightSummary(tailNumber, from, to);
    const summaries = Array.isArray(summaryData) ? summaryData : (summaryData?.data || []);

    if (!summaries.length) {
      // Aircraft disappeared from radar — treat as landed at destination
      return 'COMPLETED';
    }

    const latest = summaries[summaries.length - 1];
    const landedAt = (latest?.dest_icao || latest?.dest_iata || '').toUpperCase();
    const expected = (flight.arrival_airport || '').toUpperCase();

    if (!landedAt || !expected) return 'COMPLETED';
    if (landedAt === expected) return 'COMPLETED';

    // Landed at a different airport
    // Note: LAYOVER (multi-leg intermediate stop) is resolved by the QuotesScreen
    // background sweep. Here we default to DIVERTED for single-leg flights.
    return 'DIVERTED';
  } catch (err) {
    console.error('[StatusAutomation] confirmLanding failed:', err);
    return null; // don't transition on error
  }
}
